import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';

import { Subject } from 'rxjs';
import { delay, switchMap, take, takeUntil } from 'rxjs/operators';

import { ApiQuery, BaseContainer, SortInfo } from '@stylehaven/components';
import { ApiFragment } from '../../../record/entities/api-fragment.entity';
import { ColumnDef } from '../../../record/entities/column-definition.entity';
import { PrintService } from '../../services/print.service';

@Component({
    standalone: false,
    selector: 'digihaven-record-list-print-overview',
    templateUrl: './record-list-print-overview.component.html',
    styleUrls: ['./record-list-print-overview.component.scss']
})
export class RecordListPrintOverviewComponent extends BaseContainer implements OnDestroy, AfterViewChecked {
    // 100 because it's the max amount for fetching a page of records in the engine
    private readonly MAX_AMOUNT_OF_RECORDS = 100;

    private viewChecked$: Subject<void> = new Subject<void>();

    startLoad$: Subject<void> = new Subject<void>();

    @ViewChild('printTemplate', { read: ElementRef })
    private printTemplate!: ElementRef<HTMLDivElement>;

    @Input()
    public query!: ApiQuery;

    @Input()
    public sort!: SortInfo;

    @Input()
    public columns!: ColumnDef<ApiFragment>[];

    @Input()
    public titleTranslationKey!: string;

    @Input()
    public resultsTranslationKey!: string;

    @Output()
    public closed: EventEmitter<void> = new EventEmitter<void>();

    constructor(private printService: PrintService) {
        super();
    }

    mapCountToTitle: (count: number) => string = count => {
        const countOnList = Math.min(count, this.MAX_AMOUNT_OF_RECORDS);
        return this.translateService.instant(this.resultsTranslationKey, { items: countOnList });
    };

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.startLoad$.complete();
    }

    ngAfterViewChecked(): void {
        this.viewChecked$.next();
    }

    recordsLoaded(records: any): void {
        this.viewChecked$
            .pipe(
                take(1),
                delay(1),
                switchMap(() => this.printService.print(this.printTemplate.nativeElement)),
                takeUntil(this.closed)
            )
            .subscribe(() => {
                this.closed.next();
            });
    }

    get amountOfRecords(): number {
        return this.MAX_AMOUNT_OF_RECORDS;
    }
}
